import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import useLMSAuth, { isAdmin } from '../hooks/useLMSAuth';
import InstructorSection, { DEFAULT_INSTRUCTORS, normalizeInstructors } from '../components/InstructorSection';

const PageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.neutral[0]};
`;

const Hero = styled.section`
  padding: 82px 0 46px;
  background: #082f36;
  color: #fff;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 2.35rem;
  margin: 0 0 12px;
  font-weight: 900;
`;

const HeroSub = styled.p`
  font-size: 1.02rem;
  opacity: 0.82;
  margin: 0;
`;

const PrimaryBtn = styled.button`
  padding: 10px 20px;
  font-size: 0.95rem;
  font-weight: 800;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primary[700]};
  color: #fff;
  border: none;
  cursor: pointer;
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 28px;
  width: 90%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
`;

const Field = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.85rem;
  font-weight: 700;
  margin-bottom: 6px;
  color: #374151;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.95rem;
  box-sizing: border-box;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.95rem;
  box-sizing: border-box;
  resize: vertical;
  min-height: 80px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const SecondaryBtn = styled.button`
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  cursor: pointer;
`;

const AdminBar = styled.div`
  display: flex;
  justify-content: center;
  margin: 24px 0 0;
`;

const empty = {
  user_id: '',
  name: '',
  title: '',
  specialty: '',
  bio: '',
  photo_url: '',
  email: '',
  phone: '',
  career: '',
};

export default function InstructorsPage() {
  const { role } = useLMSAuth();
  const [list, setList] = useState(DEFAULT_INSTRUCTORS);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    try {
      const r = await fetch('/lms/instructors');
      if (!r.ok) return;
      const data = await r.json();
      setList(normalizeInstructors(data));
    } catch {
      setList(DEFAULT_INSTRUCTORS);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEdit(empty);
    setEditId(null);
    setOpen(true);
  };

  const openEdit = (instructor) => {
    setEdit({ ...empty, ...instructor });
    setEditId(instructor.id);
    setOpen(true);
  };

  const save = async () => {
    if (!edit.user_id || !edit.name) {
      alert('아이디와 이름은 필수입니다.');
      return;
    }

    const url = editId ? `/lms/instructors/${editId}` : '/lms/instructors';
    const r = await fetch(url, {
      method: editId ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(edit),
    });

    if (!r.ok) {
      alert(`저장 실패: ${await r.text()}`);
      return;
    }

    setOpen(false);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    const r = await fetch(`/lms/instructors/${id}`, { method: 'DELETE', credentials: 'include' });
    if (r.ok) load();
    else alert('삭제 실패');
  };

  return (
    <PageWrapper>
      <Hero>
        <HeroTitle>강사 소개</HeroTitle>
        <HeroSub>실무 경험을 갖춘 전문 강사진을 만나보세요.</HeroSub>
        {isAdmin(role) && (
          <AdminBar>
            <PrimaryBtn onClick={openNew}>+ 강사 등록</PrimaryBtn>
          </AdminBar>
        )}
      </Hero>

      <InstructorSection
        instructors={list}
        showAdminControls={isAdmin(role)}
        onEdit={openEdit}
        onRemove={remove}
      />

      {open && (
        <Modal onClick={() => setOpen(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 18px' }}>{editId ? '강사 수정' : '강사 등록'}</h3>
            <Field>
              <Label>아이디(user_id)</Label>
              <Input
                value={edit.user_id}
                disabled={!!editId}
                onChange={(e) => setEdit({ ...edit, user_id: e.target.value })}
              />
            </Field>
            <Field>
              <Label>이름</Label>
              <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
            </Field>
            <Field>
              <Label>직책</Label>
              <Input value={edit.title || ''} onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
            </Field>
            <Field>
              <Label>전문분야</Label>
              <Input value={edit.specialty || ''} onChange={(e) => setEdit({ ...edit, specialty: e.target.value })} />
            </Field>
            <Field>
              <Label>소개</Label>
              <TextArea value={edit.bio || ''} onChange={(e) => setEdit({ ...edit, bio: e.target.value })} />
            </Field>
            <Field>
              <Label>사진 URL</Label>
              <Input value={edit.photo_url || ''} onChange={(e) => setEdit({ ...edit, photo_url: e.target.value })} />
            </Field>
            <Field>
              <Label>이메일</Label>
              <Input value={edit.email || ''} onChange={(e) => setEdit({ ...edit, email: e.target.value })} />
            </Field>
            <Field>
              <Label>전화</Label>
              <Input value={edit.phone || ''} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} />
            </Field>
            <Field>
              <Label>경력</Label>
              <TextArea value={edit.career || ''} onChange={(e) => setEdit({ ...edit, career: e.target.value })} />
            </Field>
            <ButtonRow>
              <SecondaryBtn onClick={() => setOpen(false)}>취소</SecondaryBtn>
              <PrimaryBtn onClick={save}>저장</PrimaryBtn>
            </ButtonRow>
          </ModalBox>
        </Modal>
      )}
    </PageWrapper>
  );
}
